import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Grid, IconButton, Typography, Tooltip } from '@mui/material';
import { Mic, Stop, DeleteOutline } from '@mui/icons-material';
import { Colors, isLightTheme } from '../config/default';
import { voiceNote } from '../constants/appConstants';
import { formatDuration } from '../constants/voiceNoteUtils';
import VoiceNoteTrack from './voiceNoteTrack';

/**
 * Records a voice note for the comment box.
 *
 * The recording is handed to the parent as an ordinary File, so it rides the
 * comment's existing `files` FormData part and needs no new service call. The
 * duration goes up with it because the client is the only party that knows it
 * without decoding the container server-side.
 *
 * Container is picked per browser: Chrome and Firefox record webm/opus, Safari
 * records mp4. Both are on the server's allowlist; picking the first supported
 * one is what keeps this working in all three.
 */

// Ordered by preference. Opus is the smallest by a wide margin; mp4 is here for
// Safari, which supports neither webm nor opus.
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg'
];

// mime.lookup() on the server is filename-based and answers 'video/webm' for a
// .webm file, so the extension here is not merely cosmetic — it is the fallback
// the server uses when the declared type is missing. Kept in step with the
// declared type on purpose.
const EXTENSION_FOR = (mimeType) => {
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
};

export const pickRecordingMimeType = () => {
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return '';
  const supported = PREFERRED_MIME_TYPES.find(
    (type) => window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(type)
  );
  // '' lets MediaRecorder choose for itself rather than refusing to construct.
  return supported || '';
};

export const isRecordingSupported = () =>
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder !== 'undefined' &&
  !!navigator?.mediaDevices?.getUserMedia;

const VoiceNoteRecorder = ({ onRecorded, onCleared, disabled, maxSeconds = 300 }) => {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recorded, setRecorded] = useState(null);
  const [error, setError] = useState('');

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const previewUrlRef = useRef(null);
  const elapsedRef = useRef(0);

  /**
   * Releases the microphone and every timer.
   *
   * This MUST run on unmount. The comment box lives inside a modal that
   * unmounts when it closes, so without it a user who starts recording and then
   * closes the dialog leaves the browser's recording indicator on with no way
   * to turn it off short of a reload.
   */
  const releaseStream = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        // already stopping
      }
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      releaseStream();
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [releaseStream]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecording(false);
  }, []);

  const startRecording = async () => {
    setError('');

    if (!isRecordingSupported()) {
      setError(voiceNote.UNSUPPORTED);
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (permissionError) {
      // Denial is the common case and deserves a real message — a silent no-op
      // button reads as the feature being broken.
      setError(
        permissionError?.name === 'NotAllowedError'
          ? voiceNote.PERMISSION_DENIED
          : voiceNote.MIC_UNAVAILABLE
      );
      return;
    }

    streamRef.current = stream;
    chunksRef.current = [];
    elapsedRef.current = 0;
    setElapsed(0);

    const mimeType = pickRecordingMimeType();
    let recorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch {
      releaseStream();
      setError(voiceNote.UNSUPPORTED);
      return;
    }

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      // The recorder's own mimeType is the authority — when '' was passed the
      // browser chose for itself and this is the only way to find out what.
      const actualType = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: actualType });
      chunksRef.current = [];

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      if (!blob.size) {
        setError(voiceNote.EMPTY_RECORDING);
        return;
      }

      const bareType = actualType.split(';')[0].trim();
      const file = new File([blob], `voice-note.${EXTENSION_FOR(bareType)}`, {
        type: bareType
      });
      const seconds = Math.max(1, Math.round(elapsedRef.current));

      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = URL.createObjectURL(blob);

      setRecorded({ file, seconds, previewUrl: previewUrlRef.current });
      if (onRecorded) onRecorded(file, seconds);
    };

    recorder.start();
    recorderRef.current = recorder;
    setRecording(true);

    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
      // Hard stop. The server caps bytes; this caps length, which is the limit
      // a person actually notices.
      if (elapsedRef.current >= maxSeconds) stopRecording();
    }, 1000);
  };

  const clearRecording = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setRecorded(null);
    setElapsed(0);
    elapsedRef.current = 0;
    setError('');
    if (onCleared) onCleared();
  };

  if (!isRecordingSupported()) return null;

  const surface = isLightTheme() ? Colors.BG_BLACK : Colors.NAV_BLACK;

  return (
    <Grid container direction="column" sx={{ marginTop: '0.75em' }}>
      {/* Once there is something to hear, the row BECOMES the player rather
          than gaining a second one below it — the same transport the posted
          comment will show, so a recording looks the same before and after it
          is sent. */}
      {recorded ? (
        <VoiceNoteTrack
          src={recorded.previewUrl}
          durationSeconds={recorded.seconds}
          fileName={recorded.file?.name}>
          <Tooltip title={voiceNote.DISCARD}>
            <IconButton
              size="small"
              sx={{ color: Colors.RED }}
              aria-label="discard-voice-note"
              onClick={clearRecording}>
              <DeleteOutline sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
        </VoiceNoteTrack>
      ) : (
        <Grid
          container
          alignItems="center"
          sx={{ backgroundColor: surface, padding: '5px 10px', borderRadius: '10px' }}>
          <Tooltip title={recording ? voiceNote.STOP : voiceNote.RECORD}>
            <span>
              <IconButton
                onClick={recording ? stopRecording : startRecording}
                disabled={disabled}
                sx={{ color: recording ? Colors.RED : Colors.WHITE }}>
                {recording ? <Stop /> : <Mic />}
              </IconButton>
            </span>
          </Tooltip>

          <Typography
            variant="body2"
            sx={{ color: Colors.WHITE, flex: 1, fontSize: '0.8rem', marginLeft: '0.25em' }}>
            {recording
              ? `${voiceNote.RECORDING} ${formatDuration(elapsed)} / ${formatDuration(maxSeconds)}`
              : voiceNote.PROMPT}
          </Typography>
        </Grid>
      )}

      {error && (
        <Typography variant="body2" sx={{ color: Colors.RED, marginTop: '0.5em', fontSize: '0.8rem' }}>
          {error}
        </Typography>
      )}
    </Grid>
  );
};

export default VoiceNoteRecorder;
